import {
    ConverterLimitKind,
    ConverterLimitState,
    ProtectionDeviceState,
    ProtectionDeviceStatus,
} from '@diagram/helpers/FaultSimulationHelpers';

import { DiagramComponentInstance } from 'models';

import { IconName, resolveIconName } from 'components/component-icons/icon-names';

type ProtectionSymbolSet = { closed: IconName; cleared: IconName; unknown: IconName };

const PROTECTION_STATE_COLORS: Partial<Record<ProtectionDeviceStatus, string>> = {
    'tripped': 'var(--mantine-color-red-6)',
    'tripped-time-unknown': 'var(--mantine-color-red-6)',
    'inconclusive': 'var(--mantine-color-orange-6)',
    'not-reported': 'var(--mantine-color-orange-6)',
    'not-modelled': 'var(--mantine-color-gray-6)',
};

const ENGINE_DECIDED: Record<ProtectionDeviceStatus, boolean> = {
    'tripped': true,
    'tripped-time-unknown': true,
    'did-not-trip': true,
    'inconclusive': true,
    'not-modelled': false,
    'not-reported': false,
};

const PROTECTION_SYMBOLS: Partial<Record<IconName, ProtectionSymbolSet>> = {
    breaker: { closed: 'breakerClosed', cleared: 'breaker', unknown: 'breakerUnknown' },
    efuseBreaker: { closed: 'efuseBreaker', cleared: 'efuseBreakerCleared', unknown: 'efuseBreakerUnknown' },
    fuse: { closed: 'fuse', cleared: 'fuseCleared', unknown: 'fuseUnknown' },
    converter: { closed: 'converter', cleared: 'converterUndervoltage', unknown: 'converterUnknown' },
};

const getSymbol = (state: ProtectionDeviceState, symbols: ProtectionSymbolSet): IconName | undefined => {
    switch (state.status) {
        case 'tripped':
            return state.open ? symbols.cleared : symbols.closed;
        case 'tripped-time-unknown':
            return symbols.cleared;
        case 'did-not-trip':
            return symbols.closed;
        case 'inconclusive':
        case 'not-reported':
            return symbols.unknown;
        default:
            return undefined;
    }
};

/**
 * The phenomena dcide-sim's converter entry can decide. It reports one reason, `undervoltage`;
 * `PROTECTION_OUTCOME_REASONS` carries no over-voltage member at all, and `DC.current.max` is
 * read there as a source rating, never as a converter threshold.
 */
const ENGINE_DECIDED_LIMITS: ConverterLimitKind[] = ['undervoltage'];

const ProtectionSymbolHelpers = {
    /**
     * The derived converter indications that survive the engine's verdict — a converter whose
     * terminal left a limit it declared on its DC port. One stands down only against a verdict
     * about the *same* phenomenon, so the engine is never overwritten and the indication still
     * fills the gaps it leaves: a converter the engine modelled and found held is a statement
     * about its minimum only, and suppressing the rest on it would hide exactly what the engine
     * did not look at.
     *
     * The colour and the label read this, so they cannot disagree.
     */
    getIndicatedLimits: ({
        state,
        limits,
    }: {
        state: ProtectionDeviceState | null;
        limits: (ConverterLimitState | null | undefined)[];
    }): ConverterLimitState[] => {
        const engineDecided = state ? ENGINE_DECIDED[state.status] : false;

        return limits.filter(
            (limit): limit is ConverterLimitState =>
                !!limit?.tripped && !(engineDecided && ENGINE_DECIDED_LIMITS.includes(limit.kind)),
        );
    },

    /**
     * `borderColor` is set for a converter alone. Its 1x1 symbol is a thin diagonal and two
     * letters, so the colour is easy to miss at canvas scale; the enclosing border is the
     * largest surface the cell has.
     */
    getAppearance: ({
        componentInstance,
        state,
        limits = [],
    }: {
        componentInstance: DiagramComponentInstance;
        state: ProtectionDeviceState | null;
        limits?: (ConverterLimitState | null | undefined)[];
    }): { symbol?: IconName; color?: string; borderColor?: string } => {
        const indicated = ProtectionSymbolHelpers.getIndicatedLimits({ state, limits }).length > 0;

        const withBorder = (appearance: { symbol?: IconName; color?: string }) =>
            componentInstance.componentType === 'converter' && appearance.color === PROTECTION_STATE_COLORS.tripped
                ? { ...appearance, borderColor: appearance.color }
                : appearance;

        if (!state) return withBorder(indicated ? { color: PROTECTION_STATE_COLORS.tripped } : {});

        const iconName = resolveIconName({
            type: componentInstance.componentType,
            custom: componentInstance.componentIcon,
            specifications: componentInstance.specifications,
        });

        const symbols = PROTECTION_SYMBOLS[iconName];

        return withBorder({
            symbol: symbols && getSymbol(state, symbols),
            color: indicated ? PROTECTION_STATE_COLORS.tripped : PROTECTION_STATE_COLORS[state.status],
        });
    },
};

export { PROTECTION_STATE_COLORS, ProtectionSymbolHelpers };
